import { useMemo, useRef, useState } from "react";

import { CannedInlineMenu, filterCanned, useCannedResponses } from "./CannedPicker";
import { cn } from "../ui/cn";
import type { CannedResponse } from "../../types";

/**
 * A subject line with the same `#shortcut` trick the reply box has.
 *
 * The desk already writes the same subject over and over -- "Your return, ref
 * …", "Quote #… — revised" -- and had to retype it every time, while the body
 * underneath could be dropped in with three keystrokes. This is that feature
 * applied to the other half of the mail, and it is deliberately the *same*
 * gesture rather than a picker button: an agent who has learned `#` in the
 * composer already knows how to use this, and a second, different affordance
 * for the same idea is a second thing to learn.
 *
 * ## Why this is not the message editor
 *
 * The reply box is a contenteditable with a caret that can sit inside nested
 * markup, and its trigger machinery exists to cope with that. A subject is a
 * plain `<input>`: the caret is an integer, the value is a string, and
 * replacing a token is `slice`. Reusing `MessageEditor` here would mean
 * carrying all of that for a single line, and would produce HTML for a field
 * that must be plain text.
 *
 * What *is* shared is everything that has an opinion: `filterCanned` ranks the
 * matches and `CannedInlineMenu` draws them, so the two boxes rank and render
 * identically and cannot drift.
 *
 * ## Kind is not optional
 *
 * This only ever offers subject templates. A reply snippet is HTML, and
 * inserting one here would put markup into a mail header -- which is why
 * `filterCanned` takes the kind as a required argument rather than defaulting.
 */

/**
 * The `#token` immediately before the caret, if there is one.
 *
 * Anchored to the end of the text *before the caret*, so the menu follows the
 * word being typed and closes the moment the caret moves away from it. The
 * character class matches the shortcut rule the API enforces, which is what
 * makes typing a space the natural way to dismiss it.
 */
const TRIGGER = /#([a-z0-9-]*)$/i;

export function SubjectInput({
  value,
  onChange,
  placeholder,
  className,
  autoFocus,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const { data } = useCannedResponses();

  /**
   * The open trigger: what has been typed after the `#`, and where to draw
   * the menu.
   *
   * Null when there is nothing to complete. Kept in one piece of state rather
   * than three, so the menu cannot be half-open -- a query with no position is
   * a menu at the top left of the screen.
   */
  const [trigger, setTrigger] = useState<{
    query: string;
    top: number;
    left: number;
  } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const matches = useMemo(
    () => (trigger ? filterCanned(data ?? [], trigger.query, "subject") : []),
    [data, trigger],
  );

  /*
   * Re-evaluate the trigger after anything that could move the caret.
   *
   * Anchored to the whole box rather than to the caret's exact x. An input
   * gives no way to measure where inside it the caret is without cloning the
   * element and its font metrics into a hidden node, and for a one-line field
   * the difference is a few centimetres on a menu that is already directly
   * under what is being typed. The reply editor does the harder thing because
   * its caret can be anywhere in a tall box; here it cannot.
   */
  const reevaluate = (): void => {
    const element = input.current;
    if (!element) return;

    const caret = element.selectionStart ?? element.value.length;
    const match = TRIGGER.exec(element.value.slice(0, caret));

    if (!match) {
      setTrigger(null);
      return;
    }

    const box = element.getBoundingClientRect();
    setTrigger({ query: match[1] ?? "", top: box.bottom + 4, left: box.left });
    setActiveIndex(0);
  };

  /**
   * Swap the `#token` for the template's text.
   *
   * The `+ 1` is the `#` itself, which goes with the token -- leaving it
   * behind would produce "#Your return, ref …", which is the sort of thing
   * that gets emailed once and then never trusted again.
   */
  const insert = (entry: CannedResponse): void => {
    const element = input.current;
    if (!element || !trigger) return;

    const caret = element.selectionStart ?? element.value.length;
    const start = caret - trigger.query.length - 1;
    const next =
      value.slice(0, start) + (entry.subjectText ?? "") + value.slice(caret);

    onChange(next);
    setTrigger(null);

    /*
     * Put the caret after what was just inserted, on the next frame.
     *
     * React has not re-rendered with the new value yet, so setting
     * `selectionStart` now would be applied to the old string and then
     * overwritten. Without this the caret jumps to the end of the line, which
     * is wrong whenever a template is inserted into the middle of a subject.
     */
    const position = start + (entry.subjectText ?? "").length;
    requestAnimationFrame(() => {
      element.focus();
      element.setSelectionRange(position, position);
    });
  };

  return (
    <>
      <input
        ref={input}
        autoFocus={autoFocus}
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
          reevaluate();
        }}
        /* Clicking and arrowing move the caret without changing the value, and
           a menu left open over a token the caret has left is a menu that
           inserts in the wrong place. */
        onClick={reevaluate}
        onKeyUp={(event) => {
          if (event.key.startsWith("Arrow") || event.key === "Home" || event.key === "End") {
            reevaluate();
          }
        }}
        onKeyDown={(event) => {
          if (!trigger || matches.length === 0) return;

          /*
           * The menu owns these four keys while it is open, and gives them
           * straight back when it is not. Enter especially: this box sits in a
           * form, and letting Enter through while a menu is open would submit
           * the ticket instead of choosing the template being pointed at.
           */
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((index) => (index + 1) % matches.length);
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex(
              (index) => (index - 1 + matches.length) % matches.length,
            );
          } else if (event.key === "Enter" || event.key === "Tab") {
            event.preventDefault();
            const picked = matches[activeIndex];
            if (picked) insert(picked);
          } else if (event.key === "Escape") {
            /* Stopped, not just prevented: this box is usually inside a
               dialog, and Escape reaching it would close the whole thing when
               all the agent meant was "not that menu". */
            event.preventDefault();
            event.stopPropagation();
            setTrigger(null);
          }
        }}
        /* A blur closes it, but not before a click on the menu has landed --
           the menu's own buttons use onMouseDown for exactly that reason. */
        onBlur={() => window.setTimeout(() => setTrigger(null), 0)}
        className={className}
      />

      {trigger ? (
        <CannedInlineMenu
          matches={matches.slice(0, 6)}
          activeIndex={activeIndex}
          position={{ top: trigger.top, left: trigger.left }}
          onPick={insert}
        />
      ) : null}
    </>
  );
}

/** A hint line for the boxes this is used in, so the trick is discoverable. */
export function SubjectHint({ className }: { className?: string }) {
  const { data } = useCannedResponses();

  /* Only once the desk has written one. A hint about a feature with nothing
     behind it sends somebody looking for a list that is empty. */
  const has = (data ?? []).some(
    (entry) => entry.kind === "subject" && entry.isActive,
  );
  if (!has) return null;

  return (
    <span className={cn("text-faint", className)}>
      # inserts a subject template
    </span>
  );
}
